import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import ChartLegend from '../../../components/charts/ChartLegend';
import colorsForCharts from '../colors';

type Dataset = {
  name: string;
  values: number[];
  color?: string | string[];
};

type ChartData = {
  labels: string[];
  datasets: Dataset[];
  xAxis?: string;
  yAxis?: string;
};

type Props = {
  title: string;
  chartData: ChartData;
};

const TaraBarChart = ({ title, chartData }: Props): JSX.Element => {
  const labels = Array.isArray(chartData.labels) ? chartData.labels : [];
  const datasets = Array.isArray(chartData.datasets) ? chartData.datasets : [];

  // 1) Build `data` for Recharts: one object per label, with keys for each series
  const data = labels.map((label, idx) => {
    const row: Record<string, number | string> = { label };
    datasets.forEach((ds) => {
      row[ds.name] = ds.values[idx] ?? 0;
    });
    return row;
  });

  // 2) Decide a single color per series (first element if array, else fallback to our palette)
  const seriesColors = datasets.map((ds, i) => {
    if (Array.isArray(ds.color))
      return ds.color[0] || colorsForCharts[i % colorsForCharts.length];
    return ds.color ?? colorsForCharts[i % colorsForCharts.length];
  });

  return (
    <div className='pieChartComponent'>
      <h2 className='pieChartComponent__title'>{title || 'Bar Chart'}</h2>
      <div className='pieChartComponent__chart'>
        <div className='pieChartComponent__chart--container'>
          {/* ─── The actual Recharts bar chart ─── */}
          <div className='pieChartComponent__chart--container-chart'>
            {data.length > 0 && datasets.length > 0 ? (
              <ResponsiveContainer width='100%' height={300}>
                <BarChart
                  data={data}
                  margin={{ top: 20, right: 50, left: -10, bottom: 10 }}
                >
                  <XAxis
                    dataKey='label'
                    label={{
                      value: chartData.xAxis || '',
                      position: 'insideBottom',
                      offset: -5,
                    }}
                  />
                  <YAxis
                    label={{
                      value: chartData.yAxis || '',
                      angle: -90,
                      position: 'insideLeft',
                      offset: 20,
                    }}
                  />
                  <Tooltip />
                  {datasets.map((ds, i) => (
                    <Bar
                      key={ds.name}
                      dataKey={ds.name}
                      fill={seriesColors[i]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ color: 'gray', fontSize: '0.9rem' }}>
                No data available to display.
              </p>
            )}
          </div>

          {/* ─── Legend ─── */}
          {/* Single-series: one legend item per bar */}
          {datasets.length === 1 && (
            <div className='pieChartComponent__chart--container-legend'>
              <div className='pieChartComponent__chart--container-legend-content'>
                {datasets[0].values.map((val, idx) => (
                  <ChartLegend
                    key={`single-${idx}`}
                    color={seriesColors[0]}
                    label={labels[idx] || ''}
                    value={val}
                    symbol=''
                  />
                ))}
              </div>
            </div>
          )}

          {/* Multi-series: group by series name */}
          {datasets.length > 1 && (
            <div className='pieChartComponent__chart--container-legendGroup'>
              {datasets.map((ds, i) => (
                <div key={ds.name}>
                  <h4 className='pieChartComponent__chart--container-legendGroup-title'>
                    {ds.name}
                  </h4>
                  <div className='pieChartComponent__chart--container-legendGroup-content'>
                    {ds.values.map((val, idx) => (
                      <ChartLegend
                        key={`${ds.name}-${idx}`}
                        color={seriesColors[i]}
                        label={labels[idx] || ''}
                        value={val}
                        symbol=''
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaraBarChart;
